import ActionTextIcon from "@/components/icons/ActionTextIcon";
import { List } from "@/components/list/List";
import { openUrl } from "@/utils/utils";
import { Col, Divider, Row, Tooltip } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import notificationIcon from "../../../assets/images/notification.svg";
import {
  useInfiniteHeaderNotifications,
  useMarkAllNotificationsAsSeen,
  useToggleNotificationSentAndSeenStatus,
} from "../../../common/api/queries";
import { BugReportNotificationView } from "../../../modules/notifications/components/views/BugReportNotificationView";
import { NotificationStatus, NotificationTopic } from "../../../modules/notifications/enums";
import { NOTIFICATION_ROUTE_PATHS } from "../../../modules/notifications/paths";
import { Notification, SysadminBugReportAdditionalData } from "../../../modules/notifications/types";
import { getNotificationTopicPage } from "../../../modules/notifications/utils";
import { CURRENT_USER_ROUTE_PATHS } from "../../../modules/user/paths";
import styles from "./NotificationDropdown.module.scss";

interface Props {
  open: boolean;
  onDropdownClose: () => void;
}

export const NotificationDropdown = ({ open, onDropdownClose }: Props) => {
  const { t } = useTranslation();

  const [selectedBugReportNotification, setSelectedBugReportNotification] = useState<Notification>();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, isFetching } =
    useInfiniteHeaderNotifications(open);
  const toggleNotificationSentAndSeenStatus = useToggleNotificationSentAndSeenStatus();
  const markNotificationsAsSeen = useMarkAllNotificationsAsSeen();

  const notifications = useMemo(() => data?.pages.flatMap((page) => page.pageData) ?? [], [data?.pages]);
  const isMutationPending = toggleNotificationSentAndSeenStatus.isPending || markNotificationsAsSeen.isPending;

  const handleNotificationListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom <= 25 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage({ cancelRefetch: false });
    }
  };

  const handleNotificationToggleClick = (notification: Notification) => {
    toggleNotificationSentAndSeenStatus.mutate({ id: notification.id });
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.status === NotificationStatus.SENT) {
      toggleNotificationSentAndSeenStatus.mutate({ id: notification.id });
    }

    onDropdownClose();

    if (notification.data.topic === NotificationTopic.SYSADMIN_BUG_REPORT_CREATED) {
      setSelectedBugReportNotification(notification);
    } else {
      const topicPageUrl = getNotificationTopicPage(notification);

      if (topicPageUrl) {
        openUrl(topicPageUrl, "_blank");
      }
    }
  };

  return (
    <div className={styles.headerNotificationDropdown}>
      <div className={styles.headerNotificationListHeader}>
        <Row>
          <Col span={20}>
            <Tooltip title={t("user.titles.notificationSettings")}>
              <span>
                <ActionTextIcon
                  icon="settings"
                  color="blue"
                  size="huge"
                  path={generatePath(CURRENT_USER_ROUTE_PATHS.profile.to) + "?tab=notification-settings"}
                  onClick={onDropdownClose}
                />
              </span>
            </Tooltip>

            <Divider orientation="vertical" />

            <Tooltip title={t("notification.actions.reload")}>
              <span>
                <ActionTextIcon icon="reload" color="blue" onClick={() => refetch()} />
              </span>
            </Tooltip>
          </Col>
          <Col span={4} className="right-align">
            <Tooltip title={t("notification.actions.markAllAsSeen")}>
              <span>
                <ActionTextIcon
                  icon="eye"
                  color="blue"
                  size="huge"
                  disabled={isMutationPending}
                  onClick={() => markNotificationsAsSeen.mutate()}
                />
              </span>
            </Tooltip>
          </Col>
        </Row>
      </div>

      {notifications.length > 0 ? (
        <div onScroll={handleNotificationListScroll} className={styles.headerNotificationListScroll}>
          <List<Notification>
            className={styles.headerNotificationList}
            items={notifications}
            rowKey={(notification) => notification.id}
            gap={0}
            split
            loading={isFetching || isMutationPending}
            renderItem={(notification) => (
              <div
                className={classNames(
                  styles.headerNotificationListItem,
                  notification.status === NotificationStatus.SENT ? styles.headerNotificationListItemSent : "",
                )}
                onClick={() => handleNotificationClick(notification)}
              >
                <Row className={styles.title}>
                  <Col span={22}>{notification.data.title}</Col>
                  <Col span={2} className={styles.itemTitleSeenIconToggle}>
                    {notification.status === NotificationStatus.SEEN ? (
                      <Tooltip title={t("notification.actions.markAsUnseen")}>
                        <span onClick={(event) => event.stopPropagation()}>
                          <ActionTextIcon
                            icon="eye-invisible"
                            disabled={isMutationPending}
                            onClick={() => handleNotificationToggleClick(notification)}
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={t("notification.actions.markAsSeen")}>
                        <span onClick={(event) => event.stopPropagation()}>
                          <ActionTextIcon
                            icon="eye"
                            color="red"
                            disabled={isMutationPending}
                            onClick={() => handleNotificationToggleClick(notification)}
                          />
                        </span>
                      </Tooltip>
                    )}
                  </Col>
                </Row>

                <div className={styles.description}>
                  {notification.data.content}
                  <br />
                  <div className={styles.itemDescriptionTime}>
                    {notification.sentAt ? dayjs(notification.sentAt).fromNow() : undefined}
                  </div>
                </div>
              </div>
            )}
          />
        </div>
      ) : (
        <div className={styles.headerNotificationListEmpty}>
          <img src={notificationIcon} alt="no_notifications" style={{ height: 76, marginBottom: 16 }} />
          <div>{t("notification.helpers.empty")}</div>
        </div>
      )}

      <div className={styles.headerNotificationListFooter}>
        <Link to={NOTIFICATION_ROUTE_PATHS.list.to} onClick={onDropdownClose}>
          {t("notification.actions.showAll")}
        </Link>
      </div>

      {selectedBugReportNotification ? (
        <BugReportNotificationView
          open={!!selectedBugReportNotification}
          id={(selectedBugReportNotification.data.additionalData as SysadminBugReportAdditionalData).bugReportId}
          onCancelClick={() => setSelectedBugReportNotification(undefined)}
        />
      ) : undefined}
    </div>
  );
};
